import {
  AppState,
  Action,
  SearchResultPlan,
  PlanType,
  ActionType,
  SortOptions,
  PlanCategoryFilters,
} from "../../@types";
import { getPlans } from "../../api";
import { defaultState } from "../../app/store";
import {
  CONSTANTS as C,
  arePlansComparable,
  getOppositePlanType,
  logError,
  setPlanTypeParam,
  doFiltersExist,
} from "../../helpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { createGa4Event, Ga4Event } from "../../helpers/analytics";

export interface SearchResultsState {
  /** Current page index */
  currentPage: number;

  /* Calculated page count given total number of plans */
  pageCount: number;

  /** Timer Id for temporary display of plan compare error */
  plansToCompareErrorTimer: number;

  includeOriginalMedicare: boolean;
  loading: boolean;
  oldZip: string;
  otherPlansAvailable: number;
  plans: SearchResultPlan[];
  plansToCompareExceeded: boolean;
  showOriginalMedicareDrawer: boolean;
  showSwitchPlansHelp: boolean;
  total_results: number;
  availableCategories?: PlanCategoryFilters;
  initialCallMade: boolean;
}

export interface SearchResultsHelper {
  appState: AppState;
  clearPlansToCompare: () => void;
  dispatch: React.Dispatch<Action>;
  handleAddToCompareClick: (plan: SearchResultPlan) => void;
  handleRemovePlanFromCompare: (removeId: number | string) => void;
  listYearMismatch: boolean;
  onNextPage: () => void;
  onPrevPage: () => void;
  requestOppositePlans: () => Promise<void>;
  requestPlans: (page: number, scrollToTop?: boolean) => Promise<void>;
  resetToFirstPageOfResults: () => void;
  updateCompareList: (urlPageNumber: number) => void;
  updatePageState: (x: Partial<SearchResultsState>) => void;
  defaultSort: SortOptions;
}

export const useSearchResultsHelper = (
  appState: AppState,
  dispatch: React.Dispatch<Action>,
  pageState: SearchResultsState,
  setPageState?: React.Dispatch<React.SetStateAction<SearchResultsState>>,
  updatePageQuery?: (page: number) => void
): SearchResultsHelper => {
  const searchResultsFilters =
    appState.searchResultsFilters ?? defaultState.searchResultsFilters;
  const { mctDefaultSortByTotalCost, mctCurrentPlanYear } = useFlags();
  const {
    drugCoverageFilter,
    insuranceCarrierFilter,
    planCategoriesFilter,
    planCoveragesFilter,
    sortBy,
    starRatingFilter,
  } = searchResultsFilters;
  const flags = useFlags();

  const defaultSort = mctDefaultSortByTotalCost
    ? SortOptions.ANNUAL_TOTAL
    : SortOptions.MONTHLY_PREMIUM;

  const updatePageState = (newValue: Partial<SearchResultsState>): void => {
    return setPageState && setPageState(state => ({ ...state, ...newValue }));
  };

  /**
   * This method takes the selectedFilters that have been passed into it, combines them with any pre-existing filters, and then sends these params off to [[getPlans]] in order to get more [[Plan]]s.
   * @param page If not passed in, assumes this is the first load and runs extra functions. If provided, only updates the plans and [[pageIndex]].
   */
  const requestPlans = async (page = 0, scrollToTop = true): Promise<void> => {
    if (!appState.fips) return;

    const { fips, planType, year, zipcode } = appState;

    let paramsToSend = {
      fips: fips,
      page,
      plan_category_filter: planCategoriesFilter,
      plan_type: setPlanTypeParam(
        planType || PlanType.MAPD,
        drugCoverageFilter
      ),
      sort_order: sortBy || defaultSort,
      snp_type: appState.snpFilters || [],
      year: year,
      zip: zipcode,
    };
    planCoveragesFilter &&
      planCoveragesFilter.forEach(filter => {
        paramsToSend = {
          ...paramsToSend,
          [filter]: true,
        };
      });

    const body = {
      npis: appState.pharmacies.map(pharmacy => pharmacy.npi),
      prescriptions: appState.prescriptions.map(
        ({ ndc, quantity, frequency }) => {
          return {
            ndc,
            quantity,
            frequency,
          };
        }
      ),
      lis:
        appState.year !== mctCurrentPlanYear.toString() && appState.futureLis
          ? appState.futureLis
          : appState.lis,
    };

    if (insuranceCarrierFilter && insuranceCarrierFilter.length) {
      paramsToSend["organization_name"] =
        searchResultsFilters.insuranceCarrierFilter;
    }

    if (starRatingFilter.length) {
      paramsToSend["star_rating"] = Number(
        searchResultsFilters.starRatingFilter
      );
    }

    updatePageState({ loading: true });

    try {
      const { plans = [], total_results = 0, search_summary } =
        (await getPlans(paramsToSend, body)) || {};
      const excluded = search_summary?.excluded || 0;

      const newPageState: Partial<SearchResultsState> = {
        plans: plans,
        currentPage: page,
        total_results: total_results,
        pageCount: Math.ceil((total_results - excluded) / C.apiVars.count),
        loading: false,
        initialCallMade: true,
      };
      if (
        planType !== PlanType.PDP &&
        (!doFiltersExist(searchResultsFilters) ||
          !pageState.availableCategories)
      ) {
        newPageState.availableCategories = search_summary?.categories;
      }

      updatePageState(newPageState);

      if (updatePageQuery) updatePageQuery(page);

      if (!insuranceCarrierFilter) {
        dispatch({
          type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS_CARRIERS,
          payload: search_summary?.organization_names,
        });
      }

      if (scrollToTop) {
        window.scrollTo(0, 0);
      }
    } catch (e) {
      logError("Failed to retrieve plans (SearchResultsPage/requestPlans)", e);

      updatePageState({
        plans: [],
        currentPage: 0,
        total_results: 0,
        pageCount: 0,
        loading: false,
        availableCategories: undefined,
      });

      if (updatePageQuery) updatePageQuery(0);

      dispatch({
        type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS_CARRIERS,
        payload: [],
      });
    }
  };

  const updateCurrentPage = (page: number): void => {
    updatePageState({ currentPage: page });
    if (updatePageQuery) updatePageQuery(page);
  };

  /**
   * requestOppositePlans performs a plan search to count the number of plans for the type the user is not currently looking at
   */
  const requestOppositePlans = async (): Promise<void> => {
    if (!appState.fips) return;

    const oppositePlanType = getOppositePlanType(
      appState.planType || PlanType.MAPD
    );

    try {
      // We only want the count, so we do not need to pass in as many search filters
      // Additionally, we do not want to apply other filters a user has selected MCT-1964
      // But, we do want to mirror a "default" request as much as possible; so that when/if we cache these requests, they are likely to hit the cache
      const oppositePlans = await getPlans({
        fips: appState.fips,
        page: 0,
        plan_type: setPlanTypeParam(oppositePlanType || PlanType.PDP),
        sort_order: SortOptions.MONTHLY_PREMIUM,
        year: appState.year,
        zip: appState.zipcode,
      });

      const newPageState: Partial<SearchResultsState> = {
        otherPlansAvailable: oppositePlans?.total_results,
      };
      if (oppositePlans && oppositePlanType !== PlanType.PDP) {
        newPageState.availableCategories =
          oppositePlans.search_summary?.categories;
      }

      updatePageState(newPageState);
    } catch (e) {
      updatePageState({
        otherPlansAvailable: 0,
      });

      logError(
        "Failed to retrieve plans for opposite plan type (SearchResultsPage/requestOppositePlans)",
        e
      );
    }
  };
  const clearAddPlanToCompareError = (): void => {
    clearTimeout(pageState.plansToCompareErrorTimer);
    updatePageState({ plansToCompareExceeded: false });
  };

  const compareListYearMismatch = (): boolean => {
    return appState.compareList.some(
      plan => plan.contract_year !== appState.year
    );
  };

  const listYearMismatch = compareListYearMismatch();

  const clearPlansToCompare = (): void => {
    clearAddPlanToCompareError();
    dispatch({
      type: ActionType.UPDATE_COMPARE_LIST,
      payload: [],
    });
  };

  /**
   * Handle click event to add a plan for comparison
   */
  const handleAddToCompareClick = (plan: SearchResultPlan): void => {
    if (appState.compareList.length === C.compareFooter.maxPlansToCompare) {
      const plansToCompareErrorTimer = window.setTimeout(
        clearAddPlanToCompareError,
        C.compareFooter.errorTimeout
      );
      updatePageState({
        plansToCompareErrorTimer,
        plansToCompareExceeded: true,
      });
    } else if (
      !appState.compareList.some(
        (addedPlan: SearchResultPlan) => addedPlan.id === plan.id
      )
    ) {
      createGa4Event({
        event_name: Ga4Event.ADD_PROVIDER_TO_COMPARE,
        star_rating: plan.overall_star_rating?.rating,
        mct_insurance_provider: plan.name,
        mct_insurance_type: plan.plan_type,
        search_result_count: pageState.total_results,
        flags,
        state: appState,
      });

      dispatch({
        type: ActionType.UPDATE_COMPARE_LIST,
        payload: [...appState.compareList, plan],
      });
    }
  };

  /**
   * Handle click event to remove a plan from comparison
   */
  const handleRemovePlanFromCompare = (removeId: number | string): void => {
    clearAddPlanToCompareError();
    dispatch({
      type: ActionType.UPDATE_COMPARE_LIST,
      payload: appState.compareList.filter(
        (plan: SearchResultPlan) => plan.id !== removeId
      ),
    });
  };

  const updateCompareList = (urlPageNumber: number): void => {
    const { compareList, currentCoverage, nextYearCoverage } = appState;

    if (appState.fips && appState.planType && appState.year) {
      // If the location changes, reset page index to 0
      if (appState.zipcode !== pageState.oldZip) {
        updatePageState({ oldZip: appState.zipcode });
        requestPlans(0);
      } else {
        // If there's a page index in the URL, use it (most of the time currentPage === urlPageNumber - 1).
        // This is primarily used for bookmarked search results.
        requestPlans(
          urlPageNumber >= 1 ? urlPageNumber - 1 : pageState.currentPage
        );
      }

      // Clear the compare list, if the current year does not match the contract year of some plan in the compareList
      if (compareList.length > 0 && listYearMismatch) {
        clearPlansToCompare();
      }

      // If there is a current plan, and no plans have been added to compare,
      // and the current plan type matches the current plan's type,
      // and the current plan year matches appState.year, add the
      // current plan to the list of plans to compare
      if (
        (compareList.length === 0 || listYearMismatch) &&
        currentCoverage &&
        appState.planType &&
        currentCoverage.contract_year === appState.year &&
        arePlansComparable(appState.planType, currentCoverage.plan_type)
      ) {
        dispatch({
          type: ActionType.UPDATE_COMPARE_LIST,
          payload: [currentCoverage],
        });
      }
      // Same for next year plan
      if (
        (compareList.length === 0 || listYearMismatch) &&
        nextYearCoverage &&
        appState.planType &&
        nextYearCoverage.contract_year === appState.year &&
        arePlansComparable(appState.planType, nextYearCoverage.plan_type)
      ) {
        dispatch({
          type: ActionType.UPDATE_COMPARE_LIST,
          payload: [nextYearCoverage],
        });
      }
    }
  };

  const resetToFirstPageOfResults = (): void => {
    updateCurrentPage(0);
  };

  /**
   * onNextPage() handles request for next page of plans
   */
  const onNextPage = (): void => {
    const nextPage = pageState.currentPage + 1;

    if (nextPage < pageState.pageCount) {
      requestPlans(nextPage, true);
    }
  };

  /**
   * onPrevPage() handles request for next page of plans
   */
  const onPrevPage = (): void => {
    const prevPage = pageState.currentPage - 1;

    if (prevPage >= 0) {
      requestPlans(prevPage, true);
    }
  };

  return {
    appState,
    clearPlansToCompare,
    dispatch,
    handleAddToCompareClick,
    handleRemovePlanFromCompare,
    listYearMismatch,
    onNextPage,
    onPrevPage,
    requestPlans,
    requestOppositePlans,
    resetToFirstPageOfResults,
    updateCompareList,
    updatePageState,
    defaultSort,
  };
};
